import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  projectionFailuresQueryKey,
  requeueProjection,
} from "../../api/admin-projection-api.js";
import { AdminApiError } from "../../api/admin-users-api.js";

/**
 * View-model for the per-row Retry buttons on Identity & directory (PB-4825).
 *
 * ONE `useMutation` drives N rows, which is the trap this hook exists to avoid.
 * The mutation observer is LATEST-ONLY:
 *
 *   - `useMutation().variables` holds only the most recent `mutate()` call, so a
 *     `variables`-derived pending flag would disable just the last-clicked row
 *     and RE-ENABLE an earlier one still in flight.
 *   - per-call callbacks passed to `mutate(vars, { onSettled })` live on the
 *     observer's single `mutateOptions` and are overwritten by the next call, so
 *     the first request settling would run the second row's cleanup.
 *
 * Both are the same root cause and both let two requests for one row overlap and
 * resolve out of order. So pending state is a per-`syncId` Set, and cleanup is
 * bound to each call's own `mutateAsync()` promise. Same shape as
 * `usePhaseLockToggle` in packages/solve-feature, which this follows.
 *
 * Errors are tracked per row for the same reason: `mutation.error` is also
 * latest-only, so one row's failure would otherwise render against another row.
 */
export interface ProjectionRetryController {
  readonly isRetrying: (syncId: string) => boolean;
  readonly errorFor: (syncId: string) => string | null;
  readonly retry: (syncId: string) => void;
}

export function useProjectionRetry(): ProjectionRetryController {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  /* The re-entrancy guard reads a REF, not the `pending` state. Two clicks in
     one tick both run before React re-renders, so a state-derived guard would
     let both through and fire two requests for the same row — which is the
     out-of-order hazard the guard is here to prevent. The Set state exists for
     rendering; the ref is what decides. */
  const inFlight = useRef<Set<string>>(new Set());

  const mutation = useMutation({
    mutationFn: requeueProjection,
  });

  const retry = (syncId: string): void => {
    if (inFlight.current.has(syncId)) return;
    inFlight.current.add(syncId);
    setPending((prev) => new Set(prev).add(syncId));
    setErrors((prev) => {
      if (!prev.has(syncId)) return prev;
      const next = new Map(prev);
      next.delete(syncId);
      return next;
    });

    void mutation
      .mutateAsync(syncId)
      .then(() => {
        /* The row leaves the failed set only because the list refetches — the
           server moved it back to `pending`, so the next page simply will not
           contain it. Nothing is patched into the cache by hand, so there is no
           optimistic state to roll back if the refetch disagrees. */
        void queryClient.invalidateQueries({
          queryKey: projectionFailuresQueryKey(tenantId),
        });
        void queryClient.invalidateQueries({ queryKey: ["identity-activity"] });
        void queryClient.invalidateQueries({ queryKey: ["identity-overview"] });
      })
      .catch((error: unknown) => {
        setErrors((prev) =>
          new Map(prev).set(syncId, describeRetryError(error)),
        );
      })
      .finally(() => {
        inFlight.current.delete(syncId);
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(syncId);
          return next;
        });
      });
  };

  return {
    isRetrying: (syncId) => pending.has(syncId),
    errorFor: (syncId) => errors.get(syncId) ?? null,
    retry,
  };
}

/* Keyed on the envelope's `code`, with the server's own sentence as the
   fallback — the arrangement PB-540 established and `describeAdminError` uses.
   Only codes whose stock sentence would not tell an administrator what to do
   next get curated copy here. */
const RETRY_ERROR_COPY: Readonly<Record<string, string>> = {
  projection_not_dead_letter:
    "This projection is no longer failing — it was already retried or has since succeeded.",
};

export function describeRetryError(error: unknown): string {
  const code = error instanceof AdminApiError ? error.code : undefined;
  /* Own-property check: the map is a plain literal, so a code of "constructor"
     would otherwise resolve to an inherited function and be rendered. */
  if (code !== undefined && Object.hasOwn(RETRY_ERROR_COPY, code)) {
    return RETRY_ERROR_COPY[code] ?? "Retrying this projection failed.";
  }
  if (error instanceof Error && error.message) return error.message;
  return "Retrying this projection failed.";
}
